import { useNavigate } from 'react-router-dom';
import { Button } from '@design-system/Button';
import { Link } from '@design-system/Link';
import { ChevronRightIcon } from '@/components/icons/ChevronRightIcon';
import { ScoreCard } from '@design-system/Cards/ScoreCard';
import { Skeleton } from '@design-system/Skeleton';
import { useGetHealthZonesSummary } from '@viome/viome-base/src/usecase/Health/HealthQuery';
import { HealthZonesResponse } from '@viome/viome-base/src/usecase/Health/Health.model';
import { getRankColor } from '@/utils/getViomeColor';
import { HEALTH_CONTRIBUTING_SCORE } from '@/constants/paths';
import { getL2ImageUrl } from '@utils/nutrition/getImageUrl';
import { sortDataByRankAndTitle } from '@utils/sortDataByRankAndTitle';
import { EventTracker } from '@/utils';

const handleAllContributingScoresRedirect = () => {
  EventTracker.trackEvent('health', 'health_myHealthZones_contributingScores');
  return HEALTH_CONTRIBUTING_SCORE;
};

export const HealthZonesSection = () => {
  const navigate = useNavigate();

  const { data, isLoading, isError } = useGetHealthZonesSummary();

  const navigateToConditionsPage = (id: number) => {
    return navigate(`/health/score/${id}`);
  };

  const healthZones: HealthZonesResponse = data || [];

  if (isError) {
    return;
  }

  if (isLoading || !data) {
    return (
      <div>
        <Skeleton className="h-9 w-[50%] mb-4 rounded-lg" />
        <Skeleton className="h-6 w-[70%] mb-6 rounded-lg" />
        <div className="grid lg:grid-cols-2 gap-2.5 md:grid-cols-1 mb-6">
          {Array.from({ length: 6 }).map((_, index) => (
            <Skeleton
              key={`skeleton-card-${index}`}
              className="h-28 w-full rounded-lg"
            />
          ))}
        </div>
        <Skeleton className="h-[48px] w-full rounded-lg" />
      </div>
    );
  }

  if (healthZones.length <= 0) {
    return;
  }

  return (
    <div className="mt-12">
      <h2 className="dark typography-h1 text-primary mb-2">My Health Zones</h2>
      <p className="dark typography-body text-secondary mb-6 lg:w-[35rem]">
        Learn how your microbes influences key health areas that impact both
        short-term and long-term wellness.
      </p>
      <div className="grid lg:grid-cols-2 gap-2.5 md:grid-cols-1 mb-6">
        {sortDataByRankAndTitle(healthZones).map((zone) => (
          <div key={`${zone.title}-${zone.id}`}>
            <ScoreCard
              title={zone.title}
              tag={zone.rank}
              color={getRankColor(zone.rank)}
              score={zone.score}
              icon={getL2ImageUrl(zone.image_url)}
              variant="dark"
              hasProgressBar
              onClick={() => {
                EventTracker.trackEvent(
                  'health',
                  `tap_health_myHealthZones_${zone.title.replace(/\s+/g, '')}`,
                );
                navigateToConditionsPage(zone.id);
              }}
            />
          </div>
        ))}
      </div>
      <Button
        asChild
        variant="secondary"
        className="w-full h-12 [&_svg]:!size-6 justify-between pr-3 mt-5"
      >
        <Link to={handleAllContributingScoresRedirect()}>
          Scores contributing to Health Zones
          <ChevronRightIcon height={24} width={24} />
        </Link>
      </Button>
    </div>
  );
};
